import { computed, type Ref } from 'vue'
import { resolveAuthorForBlock, useCollection } from '@/composables/useDatastore'

/**
 * Sibling navigation for blog-type detail pages (blog / op-ed / interview).
 *
 * The three "currents" content types all live in the `blogs` datastore
 * collection, distinguished by `blogTypeId` (Blog / Op-ed / Interview). Given
 * the current item, this builds the ordered list of *same-type* siblings
 * (newest → oldest by `publishDate`) and derives the previous / next item and a
 * "latest posts" list — exactly the on-page item switching the design calls for.
 *
 * `link` targets the CMS detail route for the type (`/blog|op-ed|interview/:slug`).
 */

export interface SiblingItem {
	title: string
	image: string
	link: string
	slug: string
	authors: { name: string; title: string; image: string; slug?: string }[]
}

/** Route base (and label) per blogTypeId → the CMS slug prefix. */
const TYPE_ROUTE: Record<string, string> = {
	'5cee70f48e610a3d07c75e7d': 'op-ed', // Op-ed
	'5cee70f48e610adba3c75e7e': 'interview', // Interview
	'5cee70f48e610a27a1c75ecf': 'blog', // Blog (tabs-blog)
}

/** Map an article `type` prop → its blogTypeId (fallback when the record has none). */
const TYPE_ID: Record<string, string> = {
	'op-ed': '5cee70f48e610a3d07c75e7d',
	interview: '5cee70f48e610adba3c75e7e',
	blog: '5cee70f48e610a27a1c75ecf',
}

function routeBaseFor(blogTypeId?: string, type?: string) {
	return (blogTypeId && TYPE_ROUTE[blogTypeId]) || type || 'blog'
}

/**
 * @param currentSlug  slug of the item being viewed (to locate it in the list)
 * @param blogTypeId   the current item's blogTypeId (preferred filter)
 * @param type         fallback article type ('blog' | 'op-ed' | 'interview')
 */
export function useSiblingArticles(
	currentSlug: Ref<string>,
	blogTypeId: Ref<string | undefined>,
	type: Ref<string | undefined>,
) {
	// Fetch the whole blogs collection once; filtering/sorting is cheap in-memory.
	const { items } = useCollection<any>('blogs', {
		limit: 200,
		pick: [
			'id', '_id', 'slug', 'title', 'name', 'image', 'status',
			'publishDate', 'blogTypeId', 'authors', 'authorIds',
			'authorId', 'secondAuthorId', 'thirdAuthorId',
		],
	})

	const typeId = computed(() => blogTypeId.value || TYPE_ID[type.value ?? ''] || '')
	const routeBase = computed(() => routeBaseFor(blogTypeId.value, type.value))

	/** Same-type, published, sorted newest → oldest. */
	const siblings = computed<SiblingItem[]>(() => {
		const id = typeId.value
		return items.value
			.filter(a => a?.slug && a?.status !== 'draft' && (!id || a.blogTypeId === id))
			.slice()
			.sort((a, b) => new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime())
			.map((a) => {
				const authors = (Array.isArray(a.authors) && a.authors.length
					? a.authors
					: [
							...(Array.isArray(a.authorIds) ? a.authorIds : []),
							a.authorId, a.secondAuthorId, a.thirdAuthorId,
						].map(id => resolveAuthorForBlock(id)).filter(Boolean)
				).map((author: any) => ({
					name: author.name || '',
					title: author.title || '',
					image: author.image || '',
					slug: author.slug || '',
				}))
				return {
					title: a.title || a.name || '',
					image: a.image || '/og.jpg',
					slug: a.slug,
					link: `/${routeBaseFor(a.blogTypeId, type.value)}/${a.slug}`,
					authors,
				}
			})
	})

	const currentIndex = computed(() =>
		siblings.value.findIndex(s => s.slug === currentSlug.value),
	)

	// Newest → oldest, so "previous in the list" (older) is index+1, "next" (newer) is index-1.
	const olderItem = computed(() => {
		const i = currentIndex.value
		return i >= 0 && i < siblings.value.length - 1 ? siblings.value[i + 1] : null
	})
	const newerItem = computed(() => {
		const i = currentIndex.value
		return i > 0 ? siblings.value[i - 1] : null
	})

	/** Recent siblings excluding the current item (for the "Latest Posts" rail). */
	const latest = computed(() =>
		siblings.value.filter(s => s.slug !== currentSlug.value).slice(0, 6),
	)

	return { siblings, olderItem, newerItem, latest, routeBase }
}
